import { APP_INITIALIZER, Injector, NgModule, Optional, SkipSelf } from '@angular/core';

import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';

import { FsAddressModule } from '@firestitch/address';
import {
  FS_API_REQUEST_INTERCEPTOR,
  FsApi,
  FsApiModule,
} from '@firestitch/api';
import { FS_APP_ACL_CONFIG, FsAppAclModule } from '@firestitch/app-acl';
import { FS_ATTRIBUTE_CONFIG, FsAttributeModule } from '@firestitch/attribute';
import { FsAutocompleteChipsModule } from '@firestitch/autocomplete-chips';
import { FS_BANNER_CONFIG } from '@firestitch/banner';
import {
  BuildConfig,
  FS_BUILD_CONFIG,
  FsBuildService, UpdateAction,
} from '@firestitch/build';
import { FsCommonModule } from '@firestitch/common';
import { FsCookie } from '@firestitch/cookie';
import { FsDatePickerModule } from '@firestitch/datepicker';
import { FsDialogModule } from '@firestitch/dialog';
import { DRAWER_DEFAULT_CONFIG } from '@firestitch/drawer';
import { FsFieldEditorModule } from '@firestitch/field-editor';
import { FsFileModule } from '@firestitch/file';
import { ButtonStyle, FsFilterModule } from '@firestitch/filter';
import { FsFormModule } from '@firestitch/form';
import { FsGalleryModule } from '@firestitch/gallery';
import { FsBodyModule } from '@firestitch/html';
import { FsHtmlEditorModule } from '@firestitch/html-editor';
import { FsListModule } from '@firestitch/list';
import { FS_MAP_GOOGLE_MAP_KEY } from '@firestitch/map';
import { FsMessage, FsMessageModule } from '@firestitch/message';
import { FsNavModule } from '@firestitch/nav';
import { FS_OFFLINE_CONFIG } from '@firestitch/offline';
import { FsPasswordModule } from '@firestitch/password';
import { FsPhoneModule } from '@firestitch/phone';
import { FsProgressModule } from '@firestitch/progress';
import { FsPrompt, FsPromptModule } from '@firestitch/prompt';
import { FsScrollModule } from '@firestitch/scroll';
import { FsScrollbarModule } from '@firestitch/scrollbar';
import { FsSelectionModule } from '@firestitch/selection';
import { FsSigninModule, SigninConfig } from '@firestitch/signin';
import { FsSigninSecurityModule } from '@firestitch/signin-security';
import {
  FacebookSigninProvider, FsSocialSignin,
  GoogleSigninProvider, SocialSigninConfig,
} from '@firestitch/social-signin';
import { FsStore, FsStoreModule } from '@firestitch/store';
import { FsTabsModule } from '@firestitch/tabs';
import { FsTextEditorModule } from '@firestitch/text-editor';
import { FS_TRANSFER_HANDLER, FsTransferService } from '@firestitch/transfer';
import { FsUploadModule } from '@firestitch/upload';

import { of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { environment } from '@env/environment';

import { AclRoleData } from '@app/admin/data';
import { AclRoleLevels } from '@app/common';
import { BannerData, OfflineData } from '@app/core';
import { AttributeData } from '@app/core/data';


import {
  AclService,
  SigninService,
} from './';
import { ListDefaultConfig } from './consts';
import { TransferHandler } from './handlers';
import { bannerConfig, offlineConfig, throwIfAlreadyLoaded } from './helpers';
import { attributeConfigFactory } from './helpers/attribute-config-factory';
import { ENVIRONMENT } from './injectors';
import { ApiInterceptorFactory } from './interceptors';
import { AppService, AttributeService } from './services';

@NgModule({
  imports: [
    FsAppAclModule,
    FsSelectionModule,
    FsStoreModule,
    FsPasswordModule.forRoot(),
    FsGalleryModule.forRoot(),
    FsMessageModule.forRoot(),
    FsScrollbarModule.forRoot(),
    FsTabsModule.forRoot(),
    FsPromptModule.forRoot(),
    FsFormModule.forRoot(),
    FsFieldEditorModule.forRoot(),
    FsScrollModule.forRoot(),
    FsUploadModule.forRoot(),
    FsFileModule.forRoot(),
    FsAutocompleteChipsModule.forRoot(),
    FsAttributeModule.forRoot(),
    FsTextEditorModule.forRoot(),
    FsDatePickerModule.forRoot(),
    FsPhoneModule.forRoot(),
    FsDialogModule.forRoot(),
    FsCommonModule.forRoot(),
    FsAddressModule.forRoot(),
    FsBodyModule.forRoot(),
    FsNavModule.forRoot({ watchBrowserBackButton: false }),
    FsFilterModule.forRoot({ case: 'camel', queryParam: true, chips: true }),
    FsProgressModule.forRoot({ barColor: '#1FE3B9' }),
    FsSigninSecurityModule.forRoot({ case: 'camel' }),
    FsApiModule.forRoot({ maxFileConnections: 5 }),
    FsHtmlEditorModule.forRoot({ activationKey: 'DUA2yE1G2E1A5B5B3pZGCTRSAPJWTLPLZHTQQe1JGZxC4B3A3C2B5A1C2E4F1A1==' }),
    FsSigninModule.forRoot({
      factory: (appService: AppService, signinService: SigninService): SigninConfig => {
        return {
          processSignin: (response, redirect) => signinService
            .processSignin(response, redirect),
          signinMeta: () => of({
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          }),
          showSocialSignins: true,
        };
      },
      deps: [AppService, SigninService],
    }),
    FsListModule.forRoot(ListDefaultConfig),
    FsFilterModule.forRoot({
      case: 'camel',
      buttonStyle: ButtonStyle.Flat,
      queryParam: true,
      chips: true,
      button: {
        label: '',
      },
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (
        appService: AppService,
        buildService: FsBuildService,
        socialSignin: FsSocialSignin,
      ) => {
        return () => appService.load()
          .pipe(
            tap(() => {
              buildService.listen();
            }),
            tap(() => {
              const config: SocialSigninConfig = {
                providers: [
                  {
                    provider: new GoogleSigninProvider(appService.settings.googleClientId),
                    enabled: appService.settings.googleSigninEnabled,
                  },
                  {
                    provider: new FacebookSigninProvider(appService.settings.facebookAppId),
                    enabled: appService.settings.facebookSigninEnabled,
                  },
                ]
                  .filter((item) => item.enabled)
                  .map((item) => item.provider),
              };

              socialSignin.init(config);
            }),
          );
      },
      multi: true,
      deps: [AppService, FsBuildService, FsSocialSignin],
    },
    {
      provide: FS_MAP_GOOGLE_MAP_KEY,
      useFactory: () => 'AIzaSyDAYFE9jhdO6jwMHYpKXJH_r8CvztJiwP0',
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { floatLabel: 'auto', appearance: 'outline' },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS, 
      useValue: {
        maxWidth: '95vw',
        hasBackdrop: true,
        autoFocus: false,
        closeOnNavigation: true,
        minWidth: '450px',
      } as MatDialogConfig,
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '0ms', 
        stretchTabs: false,
        dynamicHeight: false,
      } as MatTabsConfig,
    },
    {
      provide: FS_TRANSFER_HANDLER,
      useClass: TransferHandler,
      deps: [FsMessage, FsCookie],
    },
    {
      provide: FS_API_REQUEST_INTERCEPTOR,
      useFactory: ApiInterceptorFactory,
      deps: [Injector],
    },
    {
      provide: ENVIRONMENT,
      useValue: environment,
    },
    // {
    //   provide: FS_SOCIAL_SIGNIN_CONFIG,
    //   useFactory: (appService: AppService): SocialSigninConfig => {
    //     const providers = [];

    //     if (appService.settings.googleClientId) {
    //       providers.push(new GoogleSigninProvider(appService.settings.googleClientId));
    //     }

    //     if (appService.settings.facebookAppId) {
    //       providers.push(new FacebookSigninProvider(appService.settings.facebookAppId));
    //     }

    //     return { providers };
    //   },
    //   deps: [AppService],
    // },
    {
      provide: FS_BUILD_CONFIG,
      useFactory: (): BuildConfig => {
        return {
          updateAction: environment.production ?
            UpdateAction.NavigationUpdate : UpdateAction.PromptUpdate,
        };
      },
    },
    {
      provide: DRAWER_DEFAULT_CONFIG,
      useValue: {
        width: {
          side: {
            min: 300,
          },
        },
      },
    },
    {
      provide: FS_APP_ACL_CONFIG,
      useFactory: (aclRoleData: AclRoleData) => {
        return {
          permissions: aclRoleData.permissions(),
          levels: of(AclRoleLevels),
        };
      },
      deps: [AclRoleData],
    },
    {
      provide: FS_OFFLINE_CONFIG,
      useFactory: offlineConfig,
      deps: [OfflineData, AclService],
    },
    {
      provide: FS_BANNER_CONFIG,
      useFactory: bannerConfig,
      deps: [BannerData],
    },
    FsTransferService,
    FsApi,
    FsStore,
    {
      provide: FS_ATTRIBUTE_CONFIG,
      useFactory: attributeConfigFactory,
      deps: [AttributeData, AttributeService, FsPrompt, FsMessage],
    },
  ],
  exports: [
    FsProgressModule,
  ],
})
export class CoreModule {

  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    throwIfAlreadyLoaded(parentModule, 'CoreModule');
  }

}
